import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

import Button from 'components/Button';
import FormInput from 'components/FormInput';
import Modal from 'components/Modal';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import API_ENDPOINTS from 'utils/api/endpoints';
import { toastStyle } from 'utils/helper';
import { Icon } from 'utils/icons';

import './styles.scss';

const FILE_TYPE_ERROR = 'Budget import accepts CSV and JSON files.';

const validateBudgetFile = selectedFile =>
  /\.(csv|json)$/i.test(selectedFile.name)
    ? null
    : { code: 'file-invalid-type', message: FILE_TYPE_ERROR };

const BudgetImportModal = ({ show, onHide }) => {
  const {
    createTabsMutation,
    makeAciveTabMutation,
    refetchTabs,
    refetchBudgets,
  } = useHomeScreenContext();

  const [file, setFile] = useState(null);
  const [budgetTitle, setBudgetTitle] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const fileName = file?.name || '';
  const canImport = Boolean(file) && !isImporting;
  const titlePlaceholder = useMemo(() => {
    if (!fileName) return 'Use imported budget name';
    return fileName.replace(/\.(csv|json)$/i, '');
  }, [fileName]);

  const resetState = () => {
    setFile(null);
    setBudgetTitle('');
    setIsImporting(false);
  };

  const handleClose = () => {
    if (isImporting) return;
    resetState();
    onHide();
  };

  const handleFileDrop = (acceptedFiles, fileRejections) => {
    if (acceptedFiles.length + fileRejections.length > 1) {
      toast.error('Please choose one budget file at a time.', toastStyle);
      return;
    }
    if (fileRejections.length) {
      toast.error(FILE_TYPE_ERROR, toastStyle);
      return;
    }
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setBudgetTitle(
      previous => previous || selectedFile.name.replace(/\.(csv|json)$/i, '')
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'], 'application/json': ['.json'] },
    multiple: false,
    maxFiles: 1,
    disabled: !show || isImporting,
    preventDropOnDocument: show,
    noDragEventsBubbling: true,
    // Preserve the existing native file-input chooser across browsers.
    useFsAccessApi: false,
    validator: validateBudgetFile,
    onDrop: handleFileDrop,
  });

  const handleImport = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    if (budgetTitle.trim()) {
      formData.append('name', budgetTitle.trim());
    }

    setIsImporting(true);
    try {
      const response = await API_ENDPOINTS.budgets.create_budget_from_file(
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      const newBudget = response?.data?.budget;
      const warnings = response?.data?.import_summary?.warnings || [];

      if (newBudget?.id) {
        const tabResponse = await createTabsMutation.mutateAsync([
          { budget: newBudget.id },
        ]);
        const tab = tabResponse?.data?.[0];
        if (tab?.id) {
          await makeAciveTabMutation.mutateAsync(tab.id);
        }
      }

      await refetchTabs?.();
      await refetchBudgets?.();

      toast.success(
        `Budget "${newBudget?.name || budgetTitle}" imported.`,
        toastStyle
      );
      if (warnings.length) {
        toast.info(
          `${warnings.length} import note${
            warnings.length === 1 ? '' : 's'
          } saved with the budget.`,
          toastStyle
        );
      }

      resetState();
      onHide();
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      const message =
        error?.response?.data?.detail || 'Could not import this budget.';
      toast.error(message, toastStyle);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      className="budget-import-modal"
      bodyClass="budget-import-modal__body"
      centered
    >
      <div className="budget-import">
        <header className="budget-import__header">
          <div>
            <h1>Import Budget</h1>
            <span>CBP CSV, CBP JSON, or Movie Magic JSON</span>
          </div>
          <button
            type="button"
            className="budget-import__close"
            onClick={handleClose}
            aria-label="Close"
          >
            x
          </button>
        </header>

        <div
          {...getRootProps({
            className: `budget-import__file${
              isDragActive ? ' budget-import__file--drag-active' : ''
            }`,
            role: 'button',
            'aria-label': 'Choose or drop a CSV or JSON budget file',
            'aria-disabled': isImporting,
          })}
        >
          <input
            {...getInputProps({
              id: 'budget-import-file',
              'aria-label': 'Budget file',
              disabled: isImporting,
            })}
          />
          <div className="budget-import__file-label">
            <Icon name="ArrowUpFromBracketIcon" />
            <span>
              {isDragActive
                ? 'Drop CSV or JSON file here'
                : fileName || 'Choose CSV or JSON file'}
            </span>
          </div>
          <span className="budget-import__file-hint">
            {fileName
              ? 'Click or drop a file to replace it'
              : 'or drag and drop it here'}
          </span>
        </div>

        <FormInput
          label="Budget Title"
          type="text"
          value={budgetTitle}
          placeholder={titlePlaceholder}
          onChange={event => setBudgetTitle(event.target.value)}
          disabled={isImporting}
        />

        <footer className="budget-import__actions">
          <Button
            label="Cancel"
            type="button"
            variant="red-outlined"
            onClick={handleClose}
            isDisabled={isImporting}
          />
          <Button
            label={isImporting ? 'Importing...' : 'Import'}
            type="button"
            onClick={handleImport}
            isDisabled={!canImport}
          />
        </footer>
      </div>
    </Modal>
  );
};

BudgetImportModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default BudgetImportModal;
